import {
  BorrowerFileType,
  DocumentationRequiredType,
  ExtractorDocumentType,
  getAllDocumentationRequiredByLoanId,
  getRequiredFileTypesByDocumentationRequiredType,
  patchAddRequiredFileTypeToDocumentationRequired,
  patchUploadFileToDocumentationRequired,
} from '@nemuru-eng/js-api-client';

import { ExtractedFileEntry } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/_shared/contexts/MerchantPosContext';
import { convertFileToBase64 } from '@/utils/convertFileToBase64';

const EXTRACTOR_TO_BORROWER_FILE_TYPE: Partial<Record<ExtractorDocumentType, BorrowerFileType>> = {
  [ExtractorDocumentType.DniFront]: BorrowerFileType.DOCUMENT_ID_FRONT,
  [ExtractorDocumentType.DniBack]: BorrowerFileType.DOCUMENT_ID_BACK,
  [ExtractorDocumentType.DniFrontBack]: BorrowerFileType.DOCUMENT_ID_FRONT,
  [ExtractorDocumentType.NieFront]: BorrowerFileType.DOCUMENT_ID_FRONT,
  [ExtractorDocumentType.NieBack]: BorrowerFileType.DOCUMENT_ID_BACK,
  [ExtractorDocumentType.NieEuGreenCard]: BorrowerFileType.DOCUMENT_ID_FRONT,
  [ExtractorDocumentType.Payroll]: BorrowerFileType.PAYROLL,
  [ExtractorDocumentType.PensionRevaluation]: BorrowerFileType.PENSION_REVALUATION_LETTER,
  [ExtractorDocumentType.PersonalIncomeTax]: BorrowerFileType.PERSONAL_INCOME_TAX,
  [ExtractorDocumentType.BankCertificate]: BorrowerFileType.BANK_ACCOUNT_OWNERSHIP_DOCUMENTATION,
};

const waitUntilFileTypeAdded = (loanId: string, documentationRequiredId: string, fileTypeId: string): Promise<void> =>
  new Promise((resolve, reject) => {
    let attempts = 0;
    (async function poll() {
      attempts++;
      if (attempts > 5) return reject(new Error('Timeout waiting for file type to be added'));
      try {
        const allDocumentationRequired = await getAllDocumentationRequiredByLoanId({ loanId });
        const found = allDocumentationRequired.borrowers
          .find((b) => b.id === documentationRequiredId)
          ?.requiredFiles?.some((f) => f.id === fileTypeId);
        if (found) return resolve();
      } catch {}
      setTimeout(poll, 1000);
    })();
  });

export const useDocumentExtractorUpload = () => {
  const uploadExtractedFiles = async (loanId: string, extractedFiles: ExtractedFileEntry[]): Promise<void> => {
    if (!extractedFiles.length) return;

    let borrowerDocRequired: Awaited<ReturnType<typeof getAllDocumentationRequiredByLoanId>>['borrowers'][number];
    let requiredFileTypes: Awaited<ReturnType<typeof getRequiredFileTypesByDocumentationRequiredType>>;

    try {
      const allDocumentationRequired = await getAllDocumentationRequiredByLoanId({ loanId });
      borrowerDocRequired = allDocumentationRequired.borrowers[0];
      if (!borrowerDocRequired) return;

      requiredFileTypes = await getRequiredFileTypesByDocumentationRequiredType({
        documentationRequiredType: DocumentationRequiredType.BORROWER,
      });
    } catch (error) {
      console.error('[useDocumentExtractorUpload] Failed to fetch documentation required:', error);
      return;
    }

    for (const entry of extractedFiles) {
      if (!entry.documentType) continue;

      const borrowerFileType = EXTRACTOR_TO_BORROWER_FILE_TYPE[entry.documentType];
      if (!borrowerFileType) continue;

      try {
        const alreadyRequired = borrowerDocRequired.requiredFiles?.some((f) => f.name === borrowerFileType);

        if (!alreadyRequired) {
          const fileType = requiredFileTypes.find((ft) => ft.name === borrowerFileType);
          if (!fileType) continue;

          await patchAddRequiredFileTypeToDocumentationRequired({
            id: borrowerDocRequired.id,
            fileTypeId: fileType.id,
            documentationRequiredType: DocumentationRequiredType.BORROWER,
          });

          await waitUntilFileTypeAdded(loanId, borrowerDocRequired.id, fileType.id);
        }

        const base64Data = await convertFileToBase64(entry.file);
        await patchUploadFileToDocumentationRequired({
          id: borrowerDocRequired.id,
          documentationRequiredType: DocumentationRequiredType.BORROWER,
          files: [{ name: entry.file.name, type: borrowerFileType, data: base64Data }],
        });
      } catch (error) {
        console.error('[useDocumentExtractorUpload] Failed to upload file:', entry.file.name, error);
      }
    }
  };

  return { uploadExtractedFiles };
};
